/**
 * Local landmark lists per UDA, used by the home/UDA Photo Gallery as a
 * meaningful fallback (e.g. Visakhapatnam → Rushikonda, Kailasagiri, RK Beach).
 *
 * `name` – caption shown on the tile (accurate landmark name).
 * `wiki` – Wikipedia article title; the gallery pulls that page's lead image
 *          (a real, distinct photo of the landmark) via the MediaWiki API.
 * `tag`  – keyword(s) for a topical image used ONLY when the Wikipedia page has
 *          no usable image (comma-joined, no spaces).
 *
 * Titles are cross-checked to resolve to distinct Wikipedia images. These are
 * FALLBACKS: official photos uploaded for a site (images_gallery via the API)
 * replace them automatically.
 */

export interface Landmark {
  name: string;
  wiki: string;
  tag: string;
}

/** DTCP home → Andhra Pradesh state-level landmarks. */
export const STATE_LANDMARKS: Landmark[] = [
  { name: 'Amaravati Capital Region', wiki: 'Amaravati', tag: 'amaravati' },
  { name: 'Tirumala Temple', wiki: 'Tirumala Venkateswara Temple', tag: 'tirumala' },
  { name: 'Araku Valley', wiki: 'Araku Valley', tag: 'araku,valley' },
  { name: 'Borra Caves', wiki: 'Borra Caves', tag: 'borra,caves' },
  { name: 'Gandikota Canyon', wiki: 'Gandikota', tag: 'gandikota' },
  { name: 'Lepakshi Temple', wiki: 'Lepakshi', tag: 'lepakshi' },
  { name: 'Belum Caves', wiki: 'Belum Caves', tag: 'belum,caves' },
  { name: 'Ramakrishna Beach', wiki: 'Ramakrishna Beach', tag: 'visakhapatnam,beach' },
];

export const UDA_LANDMARKS: Record<string, Landmark[]> = {
  visakhapatnam: [
    { name: 'Rushikonda Beach', wiki: 'Rushikonda', tag: 'rushikonda' },
    { name: 'Kailasagiri', wiki: 'Kailasagiri', tag: 'kailasagiri' },
    { name: 'Ramakrishna Beach', wiki: 'Ramakrishna Beach', tag: 'visakhapatnam,beach' },
    { name: 'INS Kursura Submarine Museum', wiki: 'INS Kursura (S20)', tag: 'submarine,museum' },
    { name: 'Borra Caves', wiki: 'Borra Caves', tag: 'borra,caves' },
    { name: 'Araku Valley', wiki: 'Araku Valley', tag: 'araku,valley' },
    { name: 'Yarada Beach', wiki: 'Yarada', tag: 'yarada,beach' },
    { name: 'Simhachalam Temple', wiki: 'Simhachalam', tag: 'simhachalam' },
  ],
  vijayawada: [
    { name: 'Kanaka Durga Temple', wiki: 'Kanaka Durga Temple', tag: 'kanaka,durga' },
    { name: 'Prakasam Barrage', wiki: 'Prakasam Barrage', tag: 'prakasam,barrage' },
    { name: 'Bhavani Island', wiki: 'Bhavani Island', tag: 'bhavani,island' },
    { name: 'Undavalli Caves', wiki: 'Undavalli Caves', tag: 'undavalli,caves' },
    { name: 'Kondapalli Fort', wiki: 'Kondapalli Fort', tag: 'kondapalli,fort' },
    { name: 'Mogalrajapuram Caves', wiki: 'Mogalrajapuram Caves', tag: 'vijayawada' },
  ],
  guntur: [
    { name: 'Amaravati Stupa', wiki: 'Amaravati Stupa', tag: 'amaravati,stupa' },
    { name: 'Kondaveedu Fort', wiki: 'Kondaveedu Fort', tag: 'kondaveedu,fort' },
    { name: 'Uppalapadu Bird Sanctuary', wiki: 'Uppalapadu Bird Sanctuary', tag: 'uppalapadu' },
    { name: 'Mangalagiri Temple', wiki: 'Mangalagiri', tag: 'mangalagiri' },
    { name: 'Ethipothala Falls', wiki: 'Ethipothala Falls', tag: 'ethipothala' },
    { name: 'Kotappakonda', wiki: 'Kotappakonda', tag: 'kotappakonda' },
  ],
  tirupati: [
    { name: 'Sri Venkateswara Temple', wiki: 'Tirumala Venkateswara Temple', tag: 'tirumala,temple' },
    { name: 'Kapila Theertham', wiki: 'Kapila Theertham', tag: 'kapila,theertham' },
    { name: 'S.V. Zoological Park', wiki: 'Sri Venkateswara Zoological Park', tag: 'tirupati,zoo' },
    { name: 'Srikalahasti Temple', wiki: 'Srikalahasti', tag: 'srikalahasti' },
    { name: 'Sri Padmavathi Temple, Tiruchanur', wiki: 'Tiruchanur', tag: 'tiruchanur' },
    { name: 'Sri Govindaraja Temple', wiki: 'Govindaraja Temple, Tirupati', tag: 'tirupati,temple' },
  ],
  rajahmundry: [
    { name: 'Godavari River', wiki: 'Godavari River', tag: 'godavari,river' },
    { name: 'Havelock Bridge', wiki: 'Havelock Bridge', tag: 'rajahmundry,bridge' },
    { name: 'Dowleswaram Barrage', wiki: 'Dowleswaram Barrage', tag: 'dowleswaram' },
    { name: 'Rajahmundry', wiki: 'Rajahmundry', tag: 'rajahmundry' },
    { name: 'Godavari Arch Bridge', wiki: 'Godavari Arch Bridge', tag: 'godavari,bridge' },
    { name: 'Kadiyam Nurseries', wiki: 'Kadiyam', tag: 'kadiyam,nursery' },
  ],
  kakinada: [
    { name: 'Coringa Wildlife Sanctuary', wiki: 'Coringa Wildlife Sanctuary', tag: 'coringa' },
    { name: 'Hope Island', wiki: 'Hope Island (India)', tag: 'hope,island' },
    { name: 'Uppada Beach', wiki: 'Uppada', tag: 'uppada,beach' },
    { name: 'Kakinada', wiki: 'Kakinada', tag: 'kakinada,beach' },
    { name: 'Pithapuram', wiki: 'Pithapuram', tag: 'pithapuram' },
    { name: 'Adurru', wiki: 'Adurru', tag: 'kakinada' },
  ],
  nellore: [
    { name: 'Nelapattu Bird Sanctuary', wiki: 'Nelapattu Bird Sanctuary', tag: 'nelapattu' },
    { name: 'Pulicat Lake', wiki: 'Pulicat Lake', tag: 'pulicat,lake' },
    { name: 'Mypadu Beach', wiki: 'Mypadu Beach', tag: 'mypadu,beach' },
    { name: 'Nellore', wiki: 'Nellore', tag: 'nellore,temple' },
    { name: 'Udayagiri Fort', wiki: 'Udayagiri, Nellore district', tag: 'udayagiri,fort' },
    { name: 'Penchalakona', wiki: 'Penchalakona', tag: 'penchalakona' },
  ],
  kurnool: [
    { name: 'Belum Caves', wiki: 'Belum Caves', tag: 'belum,caves' },
    { name: 'Mantralayam', wiki: 'Mantralayam', tag: 'mantralayam' },
    { name: 'Konda Reddy Fort', wiki: 'Konda Reddy Fort', tag: 'kurnool,fort' },
    { name: 'Srisailam Temple', wiki: 'Srisailam', tag: 'srisailam' },
    { name: 'Mahanandi', wiki: 'Mahanandi', tag: 'mahanandi' },
    { name: 'Rollapadu Sanctuary', wiki: 'Rollapadu Wildlife Sanctuary', tag: 'rollapadu' },
  ],
  anantapur: [
    { name: 'Lepakshi Temple', wiki: 'Lepakshi', tag: 'lepakshi' },
    { name: 'Penukonda Fort', wiki: 'Penukonda', tag: 'penukonda,fort' },
    { name: 'Puttaparthi', wiki: 'Puttaparthi', tag: 'puttaparthi' },
    { name: 'Thimmamma Marrimanu', wiki: 'Thimmamma Marrimanu', tag: 'thimmamma,marrimanu' },
    { name: 'Gooty Fort', wiki: 'Gooty Fort', tag: 'gooty,fort' },
    { name: 'Anantapur', wiki: 'Anantapur', tag: 'anantapur' },
  ],
  eluru: [
    { name: 'Kolleru Lake', wiki: 'Kolleru Lake', tag: 'kolleru,lake' },
    { name: 'Guntupalli Caves', wiki: 'Guntupalli Group of Buddhist Monuments', tag: 'guntupalli' },
    { name: 'Dwaraka Tirumala', wiki: 'Dwaraka Tirumala', tag: 'dwaraka,tirumala' },
    { name: 'Pedavegi', wiki: 'Pedavegi', tag: 'eluru,temple' },
    { name: 'Eluru', wiki: 'Eluru', tag: 'eluru' },
    { name: 'West Godavari', wiki: 'West Godavari district', tag: 'godavari,fields' },
  ],
  ongole: [
    { name: 'Bhairavakona', wiki: 'Bhairavakona', tag: 'bhairavakona' },
    { name: 'Ongole', wiki: 'Ongole', tag: 'ongole,temple' },
    { name: 'Markapur', wiki: 'Markapur', tag: 'markapur' },
    { name: 'Kothapatnam Beach', wiki: 'Kothapatnam', tag: 'kothapatnam,beach' },
    { name: 'Prakasam District', wiki: 'Prakasam district', tag: 'ongole' },
    { name: 'Chandavaram Buddhist Site', wiki: 'Chandavaram Buddhist site', tag: 'buddhist,stupa' },
  ],
  kadapa: [
    { name: 'Gandikota Canyon', wiki: 'Gandikota', tag: 'gandikota' },
    { name: 'Vontimitta Temple', wiki: 'Vontimitta', tag: 'vontimitta' },
    { name: 'Pushpagiri Temple', wiki: 'Pushpagiri Temple Complex', tag: 'pushpagiri' },
    { name: 'Lankamalleswara Sanctuary', wiki: 'Sri Lankamalleswara Wildlife Sanctuary', tag: 'forest,andhra' },
    { name: 'Kadapa', wiki: 'Kadapa', tag: 'kadapa' },
    { name: 'Mylavaram Dam', wiki: 'Mylavaram Dam', tag: 'mylavaram,dam' },
  ],
  srikakulam: [
    { name: 'Arasavalli Sun Temple', wiki: 'Arasavalli', tag: 'arasavalli' },
    { name: 'Srikurmam Temple', wiki: 'Srikurmam', tag: 'srikurmam' },
    { name: 'Salihundam', wiki: 'Salihundam', tag: 'salihundam' },
    { name: 'Kalingapatnam Beach', wiki: 'Kalingapatnam', tag: 'kalingapatnam,beach' },
    { name: 'Telineelapuram', wiki: 'Telineelapuram', tag: 'srikakulam,birds' },
    { name: 'Srikakulam', wiki: 'Srikakulam', tag: 'srikakulam' },
  ],
  vizianagaram: [
    { name: 'Vizianagaram Fort', wiki: 'Vizianagaram Fort', tag: 'vizianagaram,fort' },
    { name: 'Vizianagaram', wiki: 'Vizianagaram', tag: 'vizianagaram' },
    { name: 'Salur', wiki: 'Salur', tag: 'salur' },
    { name: 'Bobbili Fort', wiki: 'Bobbili', tag: 'bobbili,fort' },
    { name: 'Nellimarla', wiki: 'Nellimarla', tag: 'nellimarla' },
    { name: 'Ramatheertham', wiki: 'Ramatheertham', tag: 'ramatheertham' },
  ],
  tenali: [
    { name: 'Tenali', wiki: 'Tenali', tag: 'tenali,temple' },
    { name: 'Chebrolu Temple', wiki: 'Chebrolu, Guntur district', tag: 'chebrolu,temple' },
    { name: 'Kollipara', wiki: 'Kollipara', tag: 'guntur,temple' },
    { name: 'Guntur District', wiki: 'Guntur district', tag: 'guntur' },
    { name: 'Sangam Jagarlamudi', wiki: 'Sangam Jagarlamudi', tag: 'andhra,village' },
    { name: 'Krishna River', wiki: 'Krishna River', tag: 'krishna,river' },
  ],
  narasaraopet: [
    { name: 'Kotappakonda', wiki: 'Kotappakonda', tag: 'kotappakonda' },
    { name: 'Nagarjunakonda', wiki: 'Nagarjunakonda', tag: 'nagarjunakonda' },
    { name: 'Nagarjuna Sagar', wiki: 'Nagarjuna Sagar Dam', tag: 'nagarjuna,sagar' },
    { name: 'Palnadu', wiki: 'Palnadu district', tag: 'palnadu' },
    { name: 'Narasaraopet', wiki: 'Narasaraopet', tag: 'narasaraopet' },
    { name: 'Kondaveedu Fort', wiki: 'Kondaveedu Fort', tag: 'kondaveedu,fort' },
  ],
  proddatur: [
    { name: 'Proddatur', wiki: 'Proddatur', tag: 'proddatur,temple' },
    { name: 'Penna River', wiki: 'Penna River', tag: 'pennar,river' },
    { name: 'Mylavaram Dam', wiki: 'Mylavaram Dam', tag: 'mylavaram,dam' },
    { name: 'Gandikota', wiki: 'Gandikota', tag: 'gandikota' },
    { name: 'Pushpagiri', wiki: 'Pushpagiri, Andhra Pradesh', tag: 'pushpagiri' },
    { name: 'YSR Kadapa District', wiki: 'YSR district', tag: 'kadapa' },
  ],
  hindupur: [
    { name: 'Lepakshi Temple', wiki: 'Lepakshi', tag: 'lepakshi' },
    { name: 'Veerabhadra Temple', wiki: 'Veerabhadra Temple, Lepakshi', tag: 'lepakshi,temple' },
    { name: 'Penukonda Fort', wiki: 'Penukonda', tag: 'penukonda,fort' },
    { name: 'Puttaparthi', wiki: 'Puttaparthi', tag: 'puttaparthi' },
    { name: 'Hindupur', wiki: 'Hindupur', tag: 'hindupur' },
    { name: 'Sri Sathya Sai District', wiki: 'Sri Sathya Sai district', tag: 'andhra' },
  ],
  nandyal: [
    { name: 'Mahanandi Temple', wiki: 'Mahanandi', tag: 'mahanandi' },
    { name: 'Yaganti Temple', wiki: 'Yaganti', tag: 'yaganti' },
    { name: 'Ahobilam', wiki: 'Ahobilam', tag: 'ahobilam' },
    { name: 'Belum Caves', wiki: 'Belum Caves', tag: 'belum,caves' },
    { name: 'Srisailam', wiki: 'Srisailam', tag: 'srisailam' },
    { name: 'Nandyal', wiki: 'Nandyal', tag: 'nandyal' },
  ],
  mangalagiri: [
    { name: 'Mangalagiri Temple', wiki: 'Mangalagiri', tag: 'mangalagiri,temple' },
    { name: 'Amaravati Stupa', wiki: 'Amaravati Stupa', tag: 'amaravati,stupa' },
    { name: 'Undavalli Caves', wiki: 'Undavalli Caves', tag: 'undavalli,caves' },
    { name: 'Amaravati', wiki: 'Amaravati', tag: 'amaravati' },
    { name: 'Acharya Nagarjuna University', wiki: 'Acharya Nagarjuna University', tag: 'university,campus' },
    { name: 'Kondapalli Fort', wiki: 'Kondapalli Fort', tag: 'kondapalli,fort' },
  ],
  machilipatnam: [
    { name: 'Manginapudi Beach', wiki: 'Manginapudi Beach', tag: 'manginapudi,beach' },
    { name: 'Machilipatnam', wiki: 'Machilipatnam', tag: 'machilipatnam,temple' },
    { name: 'Kalamkari Art', wiki: 'Kalamkari', tag: 'kalamkari' },
    { name: 'Krishna District', wiki: 'Krishna district', tag: 'machilipatnam' },
    { name: 'Hamsaladeevi', wiki: 'Hamsaladeevi', tag: 'beach,andhra' },
    { name: 'Kuchipudi', wiki: 'Kuchipudi', tag: 'kuchipudi,dance' },
  ],
};

/**
 * Map an official UDA code-slug (the new subdomain slug, e.g. `eluda`) to the
 * city key used by UDA_LANDMARKS above. Lets the landmark data stay keyed by
 * city while the rest of the app uses UDA codes. UDAs with no curated list
 * (vk-pcpir, amuda, bauda, chuda, pkmuda, puda) fall back to STATE_LANDMARKS.
 */
const CODE_TO_LANDMARK_KEY: Record<string, string> = {
  vmrda: 'visakhapatnam', apcrda: 'vijayawada', tuda: 'tirupati', ruda: 'rajahmundry',
  kauda: 'kakinada', nuda: 'nellore', kuda: 'kurnool', ahuda: 'anantapur',
  eluda: 'eluru', kdpuda: 'kadapa', suda: 'srikakulam', buda: 'vizianagaram',
  pauda: 'narasaraopet', muda: 'machilipatnam', onuda: 'ongole'
};

/** Landmarks for a site slug (null/unknown → state-level list). */
export const getLandmarksForSlug = (slug?: string | null): Landmark[] => {
  if (!slug) return STATE_LANDMARKS;
  const s = slug.toLowerCase();
  const key = CODE_TO_LANDMARK_KEY[s] || s;
  return UDA_LANDMARKS[key] || STATE_LANDMARKS;
};

/** Topical keyword image — used only when a landmark's Wikipedia page has none. */
export const landmarkImage = (tag: string, lock: number): string =>
  `https://loremflickr.com/800/600/${tag}?lock=${lock}`;
